import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Trophy, Clock, Instagram, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const GOLD = "#C8A832";
const INDIGO = "#5B4FE0";
const ANTON = "'Anton', Impact, 'Arial Narrow', sans-serif";
const DARK = "#0a0a0a";

/* Injects the Anton display font once per page mount. */
const CutFont = () => <style>{"@import url('https://fonts.googleapis.com/css2?family=Anton&display=swap');"}</style>;

/* Live ticking countdown, shared by the landing and brand pages. */
export function useCountdown(endIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = new Date(endIso).getTime() - now;
  if (isNaN(ms) || ms <= 0) return "Voting closed";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000),
        m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
  const p = (n) => (n < 10 ? "0" : "") + n;
  return (d > 0 ? d + "d " : "") + p(h) + "h " + p(m) + "m " + p(s) + "s";
}
export function Countdown({ endIso, style }) {
  const label = useCountdown(endIso);
  return <span style={{ fontVariantNumeric: "tabular-nums", ...style }}>{label}</span>;
}

/* Both pages share the same header + look, so bundle them in one file. */
function CutHeader() {
  return (
    <header style={{ padding: "18px 24px", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", justifyContent: "space-between", background: DARK }}>
      <Link to="/the-cut" style={{ color: "#fff", textDecoration: "none", fontSize: 22, fontFamily: ANTON, letterSpacing: ".08em", textTransform: "uppercase" }}>THE CUT</Link>
      <nav style={{ display: "flex", gap: 18, alignItems: "center" }}>
        <Link to="/the-cut/leaderboard" style={{ color: "#a8a8a8", fontSize: 12, textDecoration: "none" }}>Leaderboard</Link>
        <Link to="/the-cut/winners" style={{ color: "#a8a8a8", fontSize: 12, textDecoration: "none" }}>Past winners</Link>
        <Link to="/the-cut/me" style={{ color: "#a8a8a8", fontSize: 12, textDecoration: "none" }}>Your history</Link>
        <Link to="/the-cut/press" style={{ color: "#a8a8a8", fontSize: 12, textDecoration: "none" }}>Press</Link>
        <Link to="/storefront/apply" style={{ color: "#fff", background: INDIGO, padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: "none", letterSpacing: ".04em" }}>APPLY →</Link>
      </nav>
    </header>
  );
}

/* ==================== Landing /the-cut ==================== */
export function CutLanding() {
  const [round, setRound] = useState(null);
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    api.cutActiveRound().then(async (r) => {
      setRound(r);
      const lb = await api.cutGetLeaderboard(r.id);
      setBrands(lb.brands || []);
    }).catch(() => {});
  }, []);

  const closed = !!(round && round.status === "closed");
  const winner = brands[0];
  const preUrl = (winner && winner.preorderUrl) || "https://deluxxie.com/pages/pre-orders";

  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="cut-landing-page">
      <CutFont />
      <CutHeader />
      <section style={{ padding: "80px 24px 60px", maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: INDIGO, display: "inline-flex", alignItems: "center", gap: 6, textTransform: "uppercase" }}>
          <Sparkles size={12} /> Brand competition · {closed ? "results" : "voting live"}
        </div>
        {closed && winner && (
          <div data-testid="cut-winner-banner" style={{ marginTop: 24, background: "#141414", border: `1px solid ${INDIGO}`, borderRadius: 14, padding: "20px 22px", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "#7D73F0", fontFamily: ANTON }}>Winner · {round.name}</div>
              <div style={{ fontFamily: ANTON, fontSize: 30, textTransform: "uppercase", lineHeight: 1, marginTop: 2 }}>{winner.brandName}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{winner.totalVotes || 0} community votes</div>
            </div>
            <a href={preUrl} target="_top" data-testid="cut-winner-preorder" style={{ background: INDIGO, color: "#fff", textDecoration: "none", fontFamily: ANTON, letterSpacing: ".05em", textTransform: "uppercase", fontSize: 14, padding: "13px 22px", borderRadius: 8 }}>Pre-order now →</a>
          </div>
        )}
        <h1 style={{ fontSize: 72, fontFamily: ANTON, letterSpacing: ".005em", lineHeight: 0.95, margin: "16px 0 18px", maxWidth: 820, textTransform: "uppercase" }}>
          Eight brands.<br />One drop slot.<br /><span style={{ color: INDIGO }}>You decide.</span>
        </h1>
        <p style={{ fontSize: 16, color: "#a8a8a8", maxWidth: 620, lineHeight: 1.6 }}>
          Every round, up to 8 independent brands compete for one launch slot on Deluxxie. Vote on their products — the brand with the most cumulative votes wins.
        </p>
        {round && (
          <div style={{ marginTop: 32, display: "inline-flex", alignItems: "center", gap: 14, padding: "12px 18px", background: "#141414", border: ".5px solid #232323", borderRadius: 12 }}>
            <Clock size={14} style={{ color: INDIGO }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{round.name}</div>
              <div style={{ fontSize: 11, color: "#666" }}>{closed ? "Round closed" : <>Closes in <Countdown endIso={round.endDate} style={{ color: INDIGO }} /></>}</div>
            </div>
            <Link to="/the-cut/leaderboard" style={{ color: INDIGO, fontSize: 11, textDecoration: "none", letterSpacing: ".04em", fontWeight: 600, marginLeft: 12 }}>SEE LEADERBOARD →</Link>
          </div>
        )}
      </section>

      <section style={{ padding: "0 24px 80px", maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: INDIGO, textTransform: "uppercase", marginBottom: 18 }}>Competing brands</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }} data-testid="cut-landing-brands">
          {brands.map((b, i) => {
            const initials = (b.brandName || "?").split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
            return (
              <Link key={b.id} to={`/the-cut/${b.slug}`} data-testid={`cut-landing-card-${b.slug}`}
                style={{ display: "block", background: "#141414", border: ".5px solid #232323", borderRadius: 14, padding: 20, color: "#fff", textDecoration: "none", transition: "border-color .18s, transform .2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = INDIGO; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#232323"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: i === 0 ? INDIGO : "#1a1a1a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    {initials}
                  </div>
                  <div style={{ background: "rgba(91,79,224,.16)", color: "#9d94f2", border: ".5px solid rgba(91,79,224,.4)", padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>#{b.rank || i + 1}</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{b.brandName}</div>
                <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5, minHeight: 30 }}>{b.tagline || "—"}</div>
                <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: INDIGO }}>{b.totalVotes || 0} <span style={{ fontSize: 10, color: "#666", fontWeight: 500 }}>votes</span></div>
                  <ArrowRight size={14} style={{ color: "#666" }} />
                </div>
                {!closed && round?.endDate && (
                  <div style={{ marginTop: 10, fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: INDIGO, fontFamily: ANTON }}>
                    Closes in <Countdown endIso={round.endDate} />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ==================== Leaderboard /the-cut/leaderboard ==================== */
export function CutLeaderboardPage() {
  const [round, setRound] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.cutActiveRound();
        setRound(r);
        const lb = await api.cutGetLeaderboard(r.id);
        setBrands(lb.brands || []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    };
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const max = Math.max(1, brands[0]?.totalVotes || 1);

  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="cut-leaderboard-page">
      <CutFont />
      <CutHeader />
      <section style={{ padding: "60px 24px 40px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: INDIGO, display: "inline-flex", alignItems: "center", gap: 6, textTransform: "uppercase" }}>
          <Trophy size={12} /> The Cut · Live standings
        </div>
        <h1 style={{ fontSize: 60, fontFamily: ANTON, letterSpacing: ".005em", lineHeight: 0.95, margin: "14px 0 8px", textTransform: "uppercase" }}>Leaderboard</h1>
        {round && <div style={{ fontSize: 13, color: "#a8a8a8" }}>{round.name} · updates every 5 seconds</div>}
      </section>

      <section style={{ padding: "0 24px 80px", maxWidth: 900, margin: "0 auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Loading…</div>
        ) : (
          <div style={{ background: "#141414", border: ".5px solid #232323", borderRadius: 14, padding: 8 }} data-testid="cut-leaderboard-table">
            {brands.map((b) => {
              const pct = Math.max(3, Math.round((b.totalVotes / max) * 100));
              return (
                <Link key={b.id} to={`/the-cut/${b.slug}`} data-testid={`cut-lb-full-${b.slug}`}
                  style={{ display: "grid", gridTemplateColumns: "50px 1fr 2fr 80px", gap: 16, alignItems: "center", padding: "14px 16px", borderRadius: 10, color: "#fff", textDecoration: "none", transition: "background .15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#191919")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ fontSize: 16, fontWeight: 700, color: b.rank === 1 ? INDIGO : "#888" }}>#{b.rank}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{b.brandName}</div>
                    <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{b.tagline}</div>
                  </div>
                  <div style={{ height: 6, background: "#232323", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: INDIGO, width: `${pct}%`, transition: "width .6s" }} />
                  </div>
                  <div style={{ textAlign: "right", fontSize: 16, fontWeight: 700, color: INDIGO, fontVariantNumeric: "tabular-nums" }}>{b.totalVotes}</div>
                </Link>
              );
            })}
            {brands.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#666", fontSize: 13 }}>No brands yet.</div>}
          </div>
        )}
      </section>
    </div>
  );
}

/* ==================== Winners wall /the-cut/winners ==================== */
export function CutWinnersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let voterToken = null;
    try { voterToken = localStorage.getItem("launchpad:the-cut:voter"); } catch { /* ignore */ }
    api.cutWinnersWall(voterToken).then(setRows).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Wire OG tags for the most recent winner (for sharing to socials / press)
  useEffect(() => {
    if (rows.length === 0) return;
    const first = rows[0];
    const api_base = process.env.REACT_APP_BACKEND_URL;
    const ogUrl = `${api_base}/api/the-cut/winners/${first.round.id}/og.png`;
    const set = (k, v) => {
      let el = document.querySelector(`meta[property="${k}"]`) || document.querySelector(`meta[name="${k}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", k);
        document.head.appendChild(el);
      }
      el.setAttribute("content", v);
    };
    set("og:image", ogUrl);
    set("og:title", `${first.winner.brandName} won The Cut`);
    set("og:description", first.winner.tagline || "");
    set("og:type", "article");
    set("twitter:card", "summary_large_image");
    set("twitter:image", ogUrl);
    // Autodiscovery link for RSS
    if (!document.querySelector('link[rel="alternate"][type="application/rss+xml"]')) {
      const l = document.createElement("link");
      l.rel = "alternate"; l.type = "application/rss+xml"; l.title = "The Cut · Past Winners";
      l.href = `${api_base}/api/the-cut/winners.rss`;
      document.head.appendChild(l);
    }
  }, [rows]);

  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="cut-winners-page">
      <CutHeader />
      <section style={{ padding: "60px 24px 40px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: GOLD, display: "inline-flex", alignItems: "center", gap: 6, textTransform: "uppercase" }}>
          <Trophy size={12} /> The Cut · Winners archive
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 600, letterSpacing: "-.02em", lineHeight: 1.1, margin: "10px 0 6px" }}>Every brand voted in.</h1>
        <p style={{ fontSize: 15, color: "#a8a8a8", maxWidth: 620 }}>Every round on Deluxxie has one winner. Here they are — with their round, vote count, and shipping status.</p>
      </section>

      <section style={{ padding: "0 24px 100px", maxWidth: 900, margin: "0 auto" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", background: "#141414", border: ".5px solid #232323", borderRadius: 14 }}>
            <Trophy size={28} style={{ color: "#333", marginBottom: 12 }} />
            <div style={{ fontSize: 15, color: "#a8a8a8" }}>No rounds have closed yet. The first winner will land here.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="cut-winners-list">
            {rows.map((w) => (
              <WinnerCard key={w.round.id} entry={w} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function WinnerCard({ entry }) {
  const { round, winner, runnersUp, closedAt } = entry;
  const closed = closedAt ? new Date(closedAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—";
  const initials = (winner.brandName || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{ background: "#141414", border: ".5px solid #232323", borderRadius: 16, overflow: "hidden" }} data-testid={`cut-winner-card-${winner.slug}`}>
      <div style={{ background: `linear-gradient(90deg, ${GOLD} 0%, #e0c25a 50%, ${GOLD} 100%)`, color: DARK, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Trophy size={14} />
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase" }}>{round.name}</div>
        </div>
        <div style={{ fontSize: 11, opacity: 0.75 }}>Closed {closed}</div>
      </div>
      <div style={{ padding: "22px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div style={{ width: 62, height: 62, borderRadius: 14, background: GOLD, color: DARK, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.01em" }}>{winner.brandName}</div>
            {entry.youBacked && (
              <span data-testid={`cut-you-backed-${winner.slug}`} style={{ background: "rgba(200,168,50,.16)", color: GOLD, border: ".5px solid rgba(200,168,50,.4)", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>
                ✓ You backed the winner
              </span>
            )}
          </div>
          {winner.tagline && <div style={{ fontSize: 13, color: "#a8a8a8", marginTop: 2 }}>{winner.tagline}</div>}
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8, flexWrap: "wrap", fontSize: 11, color: "#888" }}>
            <span style={{ color: GOLD, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{winner.totalVotes} votes</span>
            {entry.youBacked && entry.supporterCount > 1 && (
              <span data-testid={`cut-supporters-${winner.slug}`}>{entry.supporterCount - 1} other{entry.supporterCount - 1 === 1 ? "" : "s"} backed it too</span>
            )}
            {winner.instagram && <a href={`https://instagram.com/${(winner.instagram || "").replace(/^@/, "")}`} target="_blank" rel="noreferrer" style={{ color: "#888", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}><Instagram size={11} /> {winner.instagram}</a>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
          {winner.preorderUrl ? (
            <a href={winner.preorderUrl} target="_blank" rel="noreferrer" style={{ background: GOLD, color: DARK, padding: "10px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, letterSpacing: ".02em" }} data-testid={`cut-winner-preorder-${winner.slug}`}>
              PRE-ORDER <ExternalLink size={11} />
            </a>
          ) : (
            <span style={{ background: "#1a1a1a", color: "#666", padding: "8px 12px", borderRadius: 8, fontSize: 10, fontWeight: 600, letterSpacing: ".08em" }}>SHIPPING SOON</span>
          )}
          <Link to={`/the-cut/${winner.slug}`} style={{ fontSize: 11, color: "#a8a8a8", textDecoration: "none" }} data-testid={`cut-winner-view-${winner.slug}`}>View brand page →</Link>
        </div>
      </div>
      {runnersUp && runnersUp.length > 0 && (
        <div style={{ padding: "14px 24px 20px", borderTop: ".5px solid #1a1a1a", display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap", fontSize: 11, color: "#666" }}>
          <span style={{ letterSpacing: ".14em", textTransform: "uppercase" }}>Runners-up</span>
          {runnersUp.map((r) => (
            <Link key={r.slug} to={`/the-cut/${r.slug}`} style={{ color: "#888", textDecoration: "none" }}>
              #{r.rank} {r.brandName} <span style={{ color: "#555", marginLeft: 4 }}>· {r.totalVotes}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== Voter journey /the-cut/me ==================== */
export function CutJourneyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let voterToken = null;
    try { voterToken = localStorage.getItem("launchpad:the-cut:voter"); } catch { /* ignore */ }
    if (!voterToken) { setLoading(false); return; }
    api.cutVoterJourney(voterToken).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="cut-journey-page">
      <CutHeader />
      <section style={{ padding: "60px 24px 40px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: GOLD, textTransform: "uppercase" }}>Your Cut history</div>
        <h1 style={{ fontSize: 44, fontWeight: 600, letterSpacing: "-.02em", lineHeight: 1.1, margin: "10px 0 6px" }}>Every brand you backed.</h1>
        {data && <p style={{ fontSize: 14, color: "#a8a8a8" }}>{data.totalVotes} vote{data.totalVotes === 1 ? "" : "s"} across {data.rounds.length} round{data.rounds.length === 1 ? "" : "s"}.</p>}
      </section>

      <section style={{ padding: "0 24px 100px", maxWidth: 900, margin: "0 auto" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Loading…</div>
          : !data || data.totalVotes === 0 ? (
            <div style={{ padding: 60, textAlign: "center", background: "#141414", border: ".5px solid #232323", borderRadius: 14 }}>
              <div style={{ fontSize: 15, color: "#a8a8a8", marginBottom: 12 }}>You haven&apos;t voted in any Cut round yet.</div>
              <Link to="/the-cut" style={{ background: GOLD, color: DARK, padding: "10px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, textDecoration: "none", letterSpacing: ".02em" }}>SEE ACTIVE ROUND →</Link>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }} data-testid="cut-journey-rounds">
              {data.rounds.map((r) => (
                <div key={r.round.id} style={{ background: "#141414", border: ".5px solid #232323", borderRadius: 14, overflow: "hidden" }} data-testid={`cut-journey-round-${r.round.id}`}>
                  <div style={{ padding: "14px 20px", borderBottom: ".5px solid #232323", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: ".02em" }}>{r.round.name}</div>
                    <div style={{ fontSize: 11, color: "#666", letterSpacing: ".08em", textTransform: "uppercase" }}>{r.round.status === "closed" ? `Closed ${new Date(r.closedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "Voting still open"}</div>
                  </div>
                  {r.youBacked.map((b) => (
                    <div key={b.brandId} style={{ padding: "16px 20px", borderTop: ".5px solid #1a1a1a", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: b.isWinner ? GOLD : "#1a1a1a", color: b.isWinner ? DARK : "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>
                        {(b.brandName || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <Link to={`/the-cut/${b.slug}`} style={{ fontSize: 14, fontWeight: 500, color: "#fff", textDecoration: "none" }}>{b.brandName}</Link>
                          {b.isWinner && <span style={{ background: "rgba(200,168,50,.18)", color: GOLD, border: ".5px solid rgba(200,168,50,.4)", padding: "3px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: ".08em" }}>WINNER</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{b.yourVotes} of your vote{b.yourVotes === 1 ? "" : "s"}</div>
                      </div>
                      {b.isWinner && b.preorderUrl ? (
                        <a href={b.preorderUrl} target="_blank" rel="noreferrer" style={{ background: GOLD, color: DARK, padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: "none", letterSpacing: ".02em" }} data-testid={`cut-journey-preorder-${b.slug}`}>PRE-ORDER →</a>
                      ) : (
                        <Link to={`/the-cut/${b.slug}`} style={{ color: "#a8a8a8", fontSize: 11, textDecoration: "none" }}>View brand →</Link>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
      </section>
    </div>
  );
}

/* ==================== Press kit /the-cut/press ==================== */
export function CutPressPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.cutPressKit().then(setRows).catch(() => {}).finally(() => setLoading(false)); }, []);
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch { toast.error("Could not copy"); }
  };
  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" }} data-testid="cut-press-page">
      <CutHeader />
      <section style={{ padding: "60px 24px 40px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".14em", color: GOLD, textTransform: "uppercase" }}>Press kit</div>
        <h1 style={{ fontSize: 44, fontWeight: 600, letterSpacing: "-.02em", lineHeight: 1.1, margin: "10px 0 6px" }}>Everything you need to write about The Cut.</h1>
        <p style={{ fontSize: 15, color: "#a8a8a8", maxWidth: 640 }}>High-res OG images, ready-to-paste blurbs, and direct links for every past winner. Full archive also available as <a href={`${process.env.REACT_APP_BACKEND_URL}/api/the-cut/winners.rss`} style={{ color: GOLD, textDecoration: "none" }}>RSS →</a></p>
      </section>
      <section style={{ padding: "0 24px 100px", maxWidth: 1000, margin: "0 auto" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Loading…</div>
          : rows.length === 0 ? <div style={{ padding: 60, textAlign: "center", background: "#141414", border: ".5px solid #232323", borderRadius: 14, color: "#a8a8a8" }}>No press kit yet — the first winner will appear here.</div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="cut-press-list">
              {rows.map((entry) => (
                <div key={entry.round.id} style={{ background: "#141414", border: ".5px solid #232323", borderRadius: 16, overflow: "hidden" }} data-testid={`cut-press-card-${entry.winner.slug}`}>
                  <img src={entry.ogImageUrl} alt="" style={{ width: "100%", display: "block", aspectRatio: "1200/630", objectFit: "cover" }} />
                  <div style={{ padding: "22px 24px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: GOLD, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase" }}>{entry.round.name}</div>
                        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{entry.winner.brandName}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <a href={entry.ogImageUrl} download={`the-cut-${entry.winner.slug}.png`} style={{ background: "#1a1a1a", color: "#e8e8e8", border: ".5px solid #333", padding: "8px 12px", borderRadius: 8, fontSize: 11, fontWeight: 500, textDecoration: "none" }} data-testid={`cut-press-download-${entry.winner.slug}`}>Download OG</a>
                        <a href={entry.brandPageUrl} target="_blank" rel="noreferrer" style={{ background: "#1a1a1a", color: "#e8e8e8", border: ".5px solid #333", padding: "8px 12px", borderRadius: 8, fontSize: 11, fontWeight: 500, textDecoration: "none" }}>Brand page ↗</a>
                      </div>
                    </div>
                    <div style={{ background: "#0a0a0a", border: ".5px solid #232323", borderRadius: 10, padding: 16, fontSize: 13, color: "#e8e8e8", lineHeight: 1.6, whiteSpace: "pre-wrap" }} data-testid={`cut-press-blurb-${entry.winner.slug}`}>{entry.blurb}</div>
                    <button onClick={() => copy(entry.blurb)} style={{ marginTop: 10, background: GOLD, color: DARK, border: 0, padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: ".02em" }} data-testid={`cut-press-copy-${entry.winner.slug}`}>COPY BLURB</button>
                  </div>
                </div>
              ))}
            </div>
        }
      </section>
    </div>
  );
}
